// ============================================================================
// KNOWLEDGE SOURCES PAGE - RAG Knowledge Base Management
// ============================================================================

import { useState, useEffect, type ReactNode } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { useAIStore } from '@/stores/aiStore'
import {
  knowledgeApi,
  type KnowledgeSource,
  type KnowledgeCategory,
  type KnowledgeStatus,
  type KnowledgeStats,
} from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AddUrlSourceDialog } from './AddUrlSourceDialog'
import { AddManualContentDialog } from './AddManualContentDialog'
import { AddFileSourceDialog } from './AddFileSourceDialog'
import {
  Blocks,
  BookOpen,
  CheckCircle,
  ClipboardList,
  FlaskConical,
  BookText,
  FileText,
  Link,
  FileType,
  PenLine,
  File,
  Upload,
  Plus,
  Search,
  Database,
  Layers,
  FolderOpen,
  Trash2,
  RotateCw,
  Play,
  Loader2,
  BookMarked,
} from 'lucide-react'

// ============================================================================
// CONSTANTS
// ============================================================================

const CATEGORY_LABELS: Record<KnowledgeCategory, string> = {
  framework: 'Framework',
  case_study: 'Case Study',
  best_practice: 'Best Practice',
  template: 'Template',
  research: 'Research',
  guide: 'Guide',
  book: 'Book',
  other: 'Other',
}

const CATEGORY_ICONS: Record<KnowledgeCategory, ReactNode> = {
  framework: <Blocks className="h-4 w-4" />,
  case_study: <BookOpen className="h-4 w-4" />,
  best_practice: <CheckCircle className="h-4 w-4" />,
  template: <ClipboardList className="h-4 w-4" />,
  research: <FlaskConical className="h-4 w-4" />,
  guide: <BookText className="h-4 w-4" />,
  book: <BookMarked className="h-4 w-4" />,
  other: <FileText className="h-4 w-4" />,
}

const STATUS_COLORS: Record<KnowledgeStatus, 'outline' | 'secondary' | 'default' | 'destructive'> = {
  pending: 'outline',
  processing: 'secondary',
  ready: 'default',
  error: 'destructive',
}

// ============================================================================
// COMPONENT
// ============================================================================

export function KnowledgeSourcesPage() {
  const { accountId } = useSessionStore()
  const { getEmbeddingConfig } = useAIStore()
  const [sources, setSources] = useState<KnowledgeSource[]>([])
  const [stats, setStats] = useState<KnowledgeStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<KnowledgeCategory | 'all'>('all')
  const [statusFilter, setStatusFilter] = useState<KnowledgeStatus | 'all'>('all')
  const [showUrlDialog, setShowUrlDialog] = useState(false)
  const [showManualDialog, setShowManualDialog] = useState(false)
  const [showFileDialog, setShowFileDialog] = useState(false)
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set())

  // Load sources and stats
  const loadData = async (showLoading = true) => {
    if (!accountId) {
      setLoading(false)
      setError('No account selected. Please log in to continue.')
      return
    }

    try {
      if (showLoading) setLoading(true)
      setError(null)

      const [sourcesResult, statsResult] = await Promise.all([
        knowledgeApi.listSources(accountId, {
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
        }),
        knowledgeApi.getStats(accountId),
      ])

      setSources(sourcesResult.sources)
      setStats(statsResult)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load knowledge sources')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [accountId, categoryFilter, statusFilter])

  // Poll for status updates when sources are processing
  useEffect(() => {
    const hasProcessing = sources.some(s => s.status === 'processing')
    if (!hasProcessing) return

    const interval = setInterval(() => {
      loadData(false) // Don't show loading spinner during polling
    }, 2000) // Poll every 2 seconds for faster progress updates

    return () => clearInterval(interval)
  }, [sources, accountId, categoryFilter, statusFilter])

  // Handle source created
  const handleSourceCreated = (source: KnowledgeSource) => {
    setSources(prev => [source, ...prev])
    setShowUrlDialog(false)
    setShowManualDialog(false)
    setShowFileDialog(false)
  }

  // Handle process source
  const handleProcessSource = async (sourceId: string) => {
    if (!accountId) return

    try {
      setProcessingIds(prev => new Set(prev).add(sourceId))

      // Get embedding config from AI settings
      const embeddingConfig = getEmbeddingConfig()

      // Validate embedding config
      if (!embeddingConfig) {
        alert('No embedding provider configured. Please configure one in Settings > AI.')
        return
      }

      await knowledgeApi.processSource(accountId, sourceId, embeddingConfig)

      // Update the source status locally
      setSources(prev =>
        prev.map(s =>
          s.id === sourceId ? { ...s, status: 'processing' as KnowledgeStatus } : s
        )
      )
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to start processing')
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev)
        next.delete(sourceId)
        return next
      })
    }
  }

  // Handle delete source
  const handleDeleteSource = async (sourceId: string) => {
    if (!accountId) return
    if (!confirm('Are you sure you want to delete this knowledge source?')) return

    try {
      await knowledgeApi.deleteSource(accountId, sourceId)
      setSources(prev => prev.filter(s => s.id !== sourceId))
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete source')
    }
  }

  // Filter sources by search
  const filteredSources = sources.filter(source =>
    source.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (source.description?.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (source.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())))
  )

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Get source type icon - using design system semantic colors
  const getSourceTypeIcon = (sourceType: string, mimeType?: string) => {
    // Check mime type first for files
    if (sourceType === 'file' && mimeType) {
      if (mimeType === 'application/pdf') {
        return <FileType className="h-5 w-5 text-error" />
      }
      if (mimeType.includes('word') || mimeType.includes('docx')) {
        return <FileText className="h-5 w-5 text-business" />
      }
      if (mimeType === 'text/markdown') {
        return <FileText className="h-5 w-5 text-text-secondary" />
      }
    }

    switch (sourceType) {
      case 'url':
        return <Link className="h-5 w-5 text-market" />
      case 'pdf':
        return <FileType className="h-5 w-5 text-error" />
      case 'docx':
        return <FileText className="h-5 w-5 text-business" />
      case 'markdown':
      case 'text':
        return <FileText className="h-5 w-5 text-text-muted" />
      case 'file':
        return <File className="h-5 w-5 text-text-muted" />
      case 'manual':
        return <PenLine className="h-5 w-5 text-solution" />
      default:
        return <FileText className="h-5 w-5 text-warm-400" />
    }
  }

  if (loading) {
    return (
      <div className="h-full overflow-y-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 bg-warm-200 rounded" />
          <div className="flex gap-4">
            <div className="h-24 w-48 bg-warm-200 rounded" />
            <div className="h-24 w-48 bg-warm-200 rounded" />
            <div className="h-24 w-48 bg-warm-200 rounded" />
          </div>
          <div className="h-32 bg-warm-200 rounded" />
          <div className="h-32 bg-warm-200 rounded" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-full overflow-y-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>Setup Required</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-text-secondary mb-4">
              To use the Knowledge Base, you need to be logged in with an account.
            </p>
            <Button onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-text">Knowledge Base</h1>
          <p className="text-text-secondary">
            Manage your RAG knowledge sources for AI-enhanced coaching
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowUrlDialog(true)}>
            <Link className="h-4 w-4 mr-2" />
            Add URL
          </Button>
          <Button variant="outline" onClick={() => setShowFileDialog(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Upload File
          </Button>
          <Button onClick={() => setShowManualDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Content
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2 text-text-muted">
                <Database className="h-4 w-4" />
                <CardDescription className="m-0">Total Sources</CardDescription>
              </div>
              <CardTitle className="text-3xl">{stats.sourceCount}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2 text-text-muted">
                <Layers className="h-4 w-4" />
                <CardDescription className="m-0">Knowledge Chunks</CardDescription>
              </div>
              <CardTitle className="text-3xl">{stats.chunkCount.toLocaleString()}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2 text-text-muted">
                <FolderOpen className="h-4 w-4" />
                <CardDescription className="m-0">Categories</CardDescription>
              </div>
              <CardTitle className="text-3xl">
                {Object.keys(stats.categories).length}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex flex-wrap gap-1">
                {Object.entries(stats.categories).map(([cat, count]) => (
                  <Badge key={cat} variant="outline" className="text-xs gap-1">
                    {CATEGORY_ICONS[cat as KnowledgeCategory]}
                    {CATEGORY_LABELS[cat as KnowledgeCategory] || cat}: {count}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px] max-w-md relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
          <Input
            placeholder="Search sources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select
          value={categoryFilter}
          onValueChange={(val) => setCategoryFilter(val as KnowledgeCategory | 'all')}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                <span className="flex items-center gap-2">
                  {CATEGORY_ICONS[value as KnowledgeCategory]}
                  {label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={statusFilter}
          onValueChange={(val) => setStatusFilter(val as KnowledgeStatus | 'all')}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="ready">Ready</SelectItem>
            <SelectItem value="error">Error</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Dialogs */}
      {showUrlDialog && (
        <AddUrlSourceDialog
          onClose={() => setShowUrlDialog(false)}
          onSourceCreated={handleSourceCreated}
        />
      )}
      {showManualDialog && (
        <AddManualContentDialog
          onClose={() => setShowManualDialog(false)}
          onSourceCreated={handleSourceCreated}
        />
      )}
      {showFileDialog && (
        <AddFileSourceDialog
          onClose={() => setShowFileDialog(false)}
          onSourceCreated={handleSourceCreated}
        />
      )}

      {/* Sources List */}
      {filteredSources.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Database className="h-12 w-12 mx-auto text-warm-400 mb-4" />
            <p className="text-text-secondary mb-4">
              {sources.length === 0
                ? 'No knowledge sources yet. Add a URL or content to get started.'
                : 'No sources match your filters.'}
            </p>
            {sources.length === 0 && (
              <div className="flex justify-center gap-2">
                <Button variant="outline" onClick={() => setShowUrlDialog(true)}>
                  <Link className="h-4 w-4 mr-2" />
                  Add URL
                </Button>
                <Button variant="outline" onClick={() => setShowFileDialog(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload File
                </Button>
                <Button onClick={() => setShowManualDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Content
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredSources.map(source => (
            <Card key={source.id} className="hover:bg-warm-50 transition-colors">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-warm-100">
                      {getSourceTypeIcon(source.sourceType, source.fileMimeType)}
                    </div>
                    <div>
                      <CardTitle className="text-base font-medium">{source.title}</CardTitle>
                      <CardDescription className="flex items-center gap-1.5 mt-0.5">
                        <span className="inline-flex items-center gap-1">
                          {CATEGORY_ICONS[source.category]}
                          {CATEGORY_LABELS[source.category]}
                        </span>
                        <span className="text-warm-400">·</span>
                        {formatDate(source.createdAt)}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={STATUS_COLORS[source.status]} className="capitalize">
                      {source.status === 'processing' && (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      )}
                      {source.status}
                    </Badge>
                    {(source.status === 'pending' || source.status === 'error') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleProcessSource(source.id)}
                        disabled={processingIds.has(source.id)}
                      >
                        {processingIds.has(source.id) ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : source.status === 'error' ? (
                          <>
                            <RotateCw className="h-4 w-4 mr-1" />
                            Retry
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4 mr-1" />
                            Process
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteSource(source.id)}
                      className="text-text-muted hover:text-error"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Processing Progress Bar */}
                {source.status === 'processing' && source.processingProgress && (
                  <div className="mb-3 p-3 rounded-lg bg-warm-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-text-secondary">
                        {source.processingProgress.message}
                      </span>
                      <span className="text-sm font-medium text-text-muted">
                        {source.processingProgress.progress}%
                      </span>
                    </div>
                    <Progress value={source.processingProgress.progress} className="h-2" />
                    {source.processingProgress.totalChunks !== undefined && (
                      <p className="mt-1.5 text-xs text-text-muted">
                        {source.processingProgress.processedChunks || 0} / {source.processingProgress.totalChunks} chunks processed
                      </p>
                    )}
                  </div>
                )}
                {source.description && (
                  <p className="text-sm text-text-secondary mb-2">
                    {source.description}
                  </p>
                )}
                <div className="flex items-center gap-4 text-sm text-text-muted">
                  {source.url && (
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline text-business flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Link className="h-3 w-3" />
                      {new URL(source.url).hostname}
                    </a>
                  )}
                  {source.fileName && (
                    <span className="flex items-center gap-1">
                      <File className="h-3 w-3" />
                      {source.fileName}
                    </span>
                  )}
                  {source.chunkCount > 0 && (
                    <span className="flex items-center gap-1">
                      <Layers className="h-3 w-3" />
                      {source.chunkCount.toLocaleString()} chunks
                    </span>
                  )}
                  {source.errorMessage && (
                    <span className="text-error">{source.errorMessage}</span>
                  )}
                </div>
                {source.tags && source.tags.length > 0 && (
                  <div className="mt-2 flex gap-1">
                    {source.tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

export default KnowledgeSourcesPage
